/**
 * One-shot "the user just chose to sign out" marker.
 *
 * `RewardsGate` always wins the post-logout redirect race: when `logout()`
 * flips the session to signed-out, the gate's effect fires before any navigation
 * the sign-out handler queued. For a *visitor* who lands on `/rewards` without a
 * session that redirect (→ `/markets` + "sign in required") is correct; for
 * an *intentional* sign-out it is wrong — they should land on the base URL.
 *
 * So instead of racing the gate, the sign-out handler sets this flag and the
 * gate consumes it to pick the right destination. SessionStorage: survives the
 * logout re-render, clears on tab close, not shared across tabs.
 */

const STORAGE_KEY = 'morfi:logout_intent';

/** Mark that the next sign-out is user-initiated. Call before `logout()`. */
export function markLogoutIntent(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, '1');
  } catch {
    // SILENT-CATCH: sessionStorage may be blocked (privacy mode); the gate then
    // falls back to its default signed-out redirect.
  }
}

/** Read and clear the flag. `true` => the sign-out was user-initiated. */
export function consumeLogoutIntent(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const value = window.sessionStorage.getItem(STORAGE_KEY);
    window.sessionStorage.removeItem(STORAGE_KEY);
    return value === '1';
  } catch {
    // SILENT-CATCH: sessionStorage may be blocked (privacy mode); a failed read
    // means no intent flag, so the gate falls back to its default redirect.
    return false;
  }
}
